import React from "react";
import { Github, Youtube, MessageCircle, Linkedin, Music2, MapPin, GraduationCap, Briefcase, ExternalLink, CheckCircle2 } from "lucide-react";
import { Panel, Overline } from "../common/Panel";

const OBJECTIVES = [
  ["Build a 3-phase monitoring node using ESP32 and PZEM-004T modules with non-invasive CT sensors.", true],
  ["Transmit data via WiFi to cloud platform at 2-second intervals (upgraded from 15s ThingSpeak to 2s HiveMQ MQTT).", true],
  ["Develop a responsive cloud dashboard displaying real-time and historical trends with authentication.", true],
  ["Integrate environmental monitoring - DS18B20 temperature + MQ-135 gas sensor with automatic fan control and buzzer alerts.", true],
  ["Implement remote hardware control via MQTT - fan, light, light hub, buzzer, camera, standby - from dashboard, AI, and IR remote.", true],
  ["Develop an AI energy assistant (Gemini 2.5 Flash) with text chat, voice call, and video call with frame analysis.", true],
  ["Collect 12 weeks of continuous data and analyze load profiles, power factor patterns, and energy consumption trends.", false],
];

const FEATURES = [
  "3-phase energy metering (PZEM-004T ×3)",
  "Real-time temperature monitoring (DS18B20)",
  "Air quality monitoring - MQ-135 gas sensor (CO₂/NH₃/smoke)",
  "Automatic PWM fan control (4 thresholds via TIP120 transistor)",
  "20×4 I²C LCD - live readings, rotates phases every 5s",
  "Remote device control over MQTT",
  "PIR motion-activated enclosure light (AM312)",
  "17-button IR remote (NEC) for hardware control",
  "AI energy assistant (conversational)",
  "AI voice call mode with continuous speech recognition",
  "AI video call with Gemini Vision frame analysis",
  "AI Face Call - RoboFace Live with animated Cortex companion",
  "Real-time facial expression mirroring via Gemini Vision",
  "Multi-provider TTS - ElevenLabs, Azure, VoiceRSS, StreamElements, Browser",
  "Slash command control shortcuts",
  "Grafana Cloud / ThingSpeak / Google Sheets integration",
  "TTGO T-Camera OV2640 snapshots via MQTT",
  "Cloudflare Pages global deployment",
  "SMS backup alerts via SIM800L (planned)",
];

const STACK_HW = [
  "ESP32-WROOM-32U", "3× PZEM-004T v3.0", "3× Split-core CT (100A)", "DS18B20", "MQ-135",
  "3× TIP120 Darlington", "20×4 I²C LCD", "AM312 PIR", "5V Active Buzzer", "IR Receiver (VS1838B)",
  "IR Remote (NEC · CR2025)", "Toggle Switch", "TTGO T-Camera (OV2640)", "80mm PWM Fan",
  "Light hub (TIP120)", "2× IR TX LEDs",
];
const STACK_PASSIVE = ["R1 4.7K", "R2 10K", "R3 1K", "R4 330Ω", "R5 4.7K", "R6 1K", "R7 330Ω", "R8 1K", "830-pt Breadboard", "5V/3A PSU", "ABS Enclosure"];
const STACK_SW = ["HiveMQ Cloud MQTT", "Gemini 2.5 Flash", "React", "FastAPI", "MongoDB", "Cloudflare Pages", "Grafana Cloud", "ThingSpeak", "Google Sheets"];

const SOCIALS = [
  { icon: Github, label: "GitHub", value: "henockhnk092-dot", href: "https://github.com/henockhnk092-dot" },
  { icon: Youtube, label: "YouTube", value: "@HNK2005", href: "https://youtube.com/@HNK2005" },
  { icon: Music2, label: "TikTok", value: "@codingfever", href: "https://tiktok.com/@codingfever" },
  { icon: MessageCircle, label: "WhatsApp", value: "+27 75 453 1797", href: "https://wa.me/27754531797" },
  { icon: Linkedin, label: "LinkedIn", value: "HNK", href: "https://linkedin.com" },
];

export default function About() {
  return (
    <div className="space-y-6">
      <div>
        <Overline>Project Information</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">About the System</h2>
      </div>

      {/* Original Specification */}
      <Panel className="p-6 space-y-4">
        <div>
          <Overline>Original Project Specification</Overline>
          <h3 className="font-heading font-bold text-lg mt-1">IoT-Based Energy and Power Quality Dashboard for Campus Buildings</h3>
          <p className="text-xs text-[var(--t2)] font-mono mt-1">Faculty of Engineering & the Built Environment · CPUT · 2026</p>
        </div>
        <p className="text-sm leading-relaxed text-[var(--t2)]">
          <span className="text-[var(--t1)] font-medium">Problem:</span> CPUT campus buildings have no real-time three-phase IoT monitoring for voltage, current, power, power factor, frequency, and energy. Commercial BMS solutions (e.g. Schneider EcoStruxure) cost over R500,000, making them unaffordable for individual departments. Monthly utility bills offer no visibility into peak consumption, phase imbalances, or power factor issues.
        </p>
        <p className="text-sm leading-relaxed text-[var(--t2)]">
          <span className="text-[var(--t1)] font-medium">Original aim:</span> Design and deploy a low-cost IoT energy monitoring system that measures voltage, current, power, power factor, and frequency in a campus building, with real-time cloud-based dashboard access. The original electronics BOM was estimated at ~R2,595. After extensions (MQ-135, 3× TIP120 drivers, buzzer, PIR, IR receiver/remote, camera, enclosure build materials, and AC load setup), the total project cost increased - but still represents a &gt;95% reduction vs commercial BMS systems costing R500,000+.
        </p>
        <div className="text-sm text-[var(--t2)] space-y-1">
          <div className="font-medium text-[var(--t1)] text-xs uppercase tracking-wider mb-2">Original Objectives (4)</div>
          <div className="flex items-start gap-2"><span className="text-[var(--accent)] font-mono text-xs mt-0.5">O1.</span> Build a three-phase monitoring node using ESP32 and PZEM-004T modules with non-invasive CT sensors.</div>
          <div className="flex items-start gap-2"><span className="text-[var(--accent)] font-mono text-xs mt-0.5">O2.</span> Transmit data via Wi-Fi to ThingSpeak at 15-second intervals.</div>
          <div className="flex items-start gap-2"><span className="text-[var(--accent)] font-mono text-xs mt-0.5">O3.</span> Develop a Grafana or ThingSpeak dashboard showing real-time and historical trends.</div>
          <div className="flex items-start gap-2"><span className="text-[var(--accent)] font-mono text-xs mt-0.5">O4.</span> Collect 4 weeks of continuous data and analyze load profiles, power factor patterns, and energy consumption trends.</div>
        </div>
        <div className="text-sm text-[var(--t2)] space-y-1 mt-3">
          <div className="font-medium text-[var(--t1)] text-xs uppercase tracking-wider mb-2">Original Deliverables</div>
          <div className="flex items-start gap-2"><span className="font-mono text-xs mt-0.5">1.</span> Deployed ESP32-based three-phase IoT monitoring prototype in a CPUT campus building.</div>
          <div className="flex items-start gap-2"><span className="font-mono text-xs mt-0.5">2.</span> Live Grafana dashboard and ThingSpeak public channels with real-time and historical energy data.</div>
          <div className="flex items-start gap-2"><span className="font-mono text-xs mt-0.5">3.</span> Four-week continuous dataset with analysed load profiles, power factor trends, and energy consumption patterns.</div>
          <div className="flex items-start gap-2"><span className="font-mono text-xs mt-0.5">4.</span> Open-source ESP32 firmware and dashboard code on GitHub.</div>
          <div className="flex items-start gap-2"><span className="font-mono text-xs mt-0.5">5.</span> Research dissertation.</div>
        </div>
      </Panel>

      {/* Extended Objectives */}
      <Panel className="p-6 space-y-4">
        <div>
          <Overline>What Henock Built - Beyond the Specification</Overline>
          <p className="text-sm text-[var(--t2)] mt-1">The original brief defined a passive energy dashboard with 4 objectives. Henock expanded the project into a comprehensive IoT command center with 7 objectives - adding environmental sensing, automated thermal management, air quality monitoring, remote hardware control, camera surveillance, and an AI-powered energy assistant with voice and video call capabilities. Data transmission was upgraded from 15-second ThingSpeak polling to 2-second HiveMQ Cloud MQTT streaming. The basic Grafana/ThingSpeak dashboard was replaced with a full React web application featuring authentication, personalization, and mobile-responsive design. The data collection target was extended from 4 weeks to 12 weeks.</p>
        </div>
        <div className="space-y-2">
          {OBJECTIVES.map(([text, done], i) => (
            <div key={i} className="flex items-start gap-2 text-sm">
              <CheckCircle2 size={15} className={`mt-0.5 shrink-0 ${done ? "text-[var(--accent)]" : "text-[var(--warning)]"}`} />
              <span className="text-[var(--t2)]"><span className="font-mono text-xs text-[var(--accent)] mr-1">{i + 1}.</span>{text}</span>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Panel className="p-6 md:col-span-2 space-y-5">
          <p className="text-sm leading-relaxed text-[var(--t2)]">
            <span className="text-[var(--t1)] font-medium">Campus Energy Monitor</span> is an IoT-based energy and power quality monitoring system for campus buildings. Originally specified as a basic metering dashboard, it evolved into a full command center with environmental sensing, automated hardware control, camera surveillance, and AI-assisted analytics. ESP32 microcontrollers paired with three PZEM-004T sensors stream live voltage, current, power, power factor and energy to HiveMQ Cloud over MQTT at 2-second intervals. A DS18B20 temperature probe drives automatic PWM fan control via a TIP120 transistor, while an MQ-135 gas sensor classifies air quality (GOOD/POOR/DANGER) with buzzer alerts. A TTGO T-Camera adds visual monitoring, and a Gemini 2.5 Flash AI assistant supports text, voice, and video interaction with live sensor context. Data feeds into Grafana Cloud, ThingSpeak, and Google Sheets for long-term analysis.
          </p>
          <div>
            <Overline>Full Feature List</Overline>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-3">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm" data-testid="about-feature">
                  <CheckCircle2 size={15} className="text-[var(--accent)] mt-0.5 shrink-0" /><span className="text-[var(--t2)]">{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <Overline>Hardware Components</Overline>
            <div className="flex flex-wrap gap-2 mt-3">
              {STACK_HW.map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full text-xs font-mono bg-[var(--accent-soft)] border border-[var(--accent)]/20 text-[var(--t1)]">{s}</span>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--t2)] font-bold mt-3 mb-1">Passive Components & Power</div>
            <div className="flex flex-wrap gap-1.5">
              {STACK_PASSIVE.map((s) => (
                <span key={s} className="px-2 py-1 rounded text-[10px] font-mono bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)]">{s}</span>
              ))}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--t2)] font-bold mt-3 mb-1">Software & Cloud</div>
            <div className="flex flex-wrap gap-2">
              {STACK_SW.map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full text-xs font-mono bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t1)]">{s}</span>
              ))}
            </div>
          </div>
        </Panel>

        {/* Developer profile */}
        <Panel className="p-6" data-testid="developer-profile">
          <div className="flex flex-col items-center text-center">
            <img
              src="/assets/henock-portrait.png"
              alt="Campus Energy Monitor hardware"
              data-testid="developer-photo"
              className="w-28 h-28 rounded-lg object-cover border-2 border-[var(--accent)] shadow-lg"
              onError={(e) => { e.currentTarget.src = "/assets/henock-resolve.png"; }}
            />
            <h3 className="font-heading font-bold text-xl mt-4">HNK Engineering</h3>
            <p className="text-[var(--accent)] font-mono text-sm">Computer Engineering Honours</p>
            <p className="text-xs text-[var(--t2)] mt-1">CPUT · Final Year 2026</p>

            <div className="w-full mt-4 space-y-2 text-xs text-[var(--t2)] font-mono">
              <div className="flex items-center justify-center gap-2"><Briefcase size={13} /> BET Hons CompEng · CPUT</div>
              <div className="flex items-center justify-center gap-2"><MapPin size={13} /> Cape Town, South Africa</div>
            </div>

            <div className="flex gap-2 mt-5 w-full">
              <a href="https://parkade-stitch-site.pages.dev" target="_blank" rel="noreferrer" data-testid="about-portfolio" className="flex-1 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium flex items-center justify-center gap-1.5">Portfolio <ExternalLink size={13} /></a>
              <a href="https://wa.me/27754531797" target="_blank" rel="noreferrer" data-testid="about-book" className="flex-1 py-2 rounded-md border border-[var(--bd)] text-sm font-medium flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors">Book Services</a>
            </div>

            <div className="w-full mt-4 space-y-1.5">
              {SOCIALS.map(({ icon: Icon, label, value, href }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" data-testid={`social-${label.toLowerCase()}`} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[var(--accent-soft)] transition-colors text-sm">
                  <Icon size={15} className="text-[var(--accent)] shrink-0" />
                  <span className="text-[var(--t2)]">{label}</span>
                  <span className="ml-auto font-mono text-xs truncate">{value}</span>
                </a>
              ))}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
